import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import {
  AppScreen,
  CallData,
  ErrorData,
  LoginData,
  OnlineUsersData,
  WebRTCSignalData,
} from "../types";
import WebSocketService from "../services/WebSocketService";
import WebRTCService from "../services/WebRTCService";
import { jwtDecode } from "jwt-decode";
import { API_ENDPOINTS } from "../config";
import { TTSService } from "../services/TTSService";

// State interface
export interface AppState {
  screen: AppScreen;
  currentUserId: string | null;
  targetUserId: string | null;
  callerId: string | null;
  conversationId: string | null;
  isConnected: boolean;
  isAudioMuted: boolean;
  callDuration: number;
  error: string | null;
  onlineUsers: string[];
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: "therapist" | "normal";
    has_paid: boolean;
    profile_photo_url?: string;
  } | null;
  ttsEnabled: boolean;
}

// Action types
type AppAction =
  | { type: "SET_SCREEN"; payload: AppScreen }
  | { type: "SET_CURRENT_USER"; payload: string | null }
  | { type: "SET_TARGET_USER"; payload: string | null }
  | { type: "SET_CALLER"; payload: string | null }
  | { type: "SET_CONVERSATION_ID"; payload: string | null }
  | { type: "SET_CONNECTED"; payload: boolean }
  | { type: "SET_AUDIO_MUTED"; payload: boolean }
  | { type: "SET_CALL_DURATION"; payload: number }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_ONLINE_USERS"; payload: string[] }
  | { type: "RESET_CALL_STATE" }
  | {
      type: "SET_USER";
      payload: {
        id: string;
        name: string;
        email: string;
        role: "therapist" | "normal";
        has_paid: boolean;
        profile_data?: any; // Therapist profile data (if therapist user)
        profile_photo_url?: string;
      };
    }
  | { type: "TOGGLE_TTS" }
  | { type: "UPDATE_PROFILE_PHOTO"; payload: string };

// Initial state
const initialState: AppState = {
  screen: "login",
  currentUserId: null,
  targetUserId: null,
  callerId: null,
  conversationId: null,
  isConnected: false,
  isAudioMuted: false,
  callDuration: 0,
  error: null,
  onlineUsers: [],
  token: "",
  user: null,
  ttsEnabled: false,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "SET_SCREEN":
      return { ...state, screen: action.payload };
    case "SET_USER":
      return { ...state, user: action.payload };
    case "SET_CURRENT_USER":
      return { ...state, currentUserId: action.payload };
    case "SET_TARGET_USER":
      return { ...state, targetUserId: action.payload };
    case "SET_CALLER":
      return { ...state, callerId: action.payload };
    case "SET_CONVERSATION_ID":
      return { ...state, conversationId: action.payload };
    case "SET_CONNECTED":
      return { ...state, isConnected: action.payload };
    case "SET_AUDIO_MUTED":
      return { ...state, isAudioMuted: action.payload };
    case "SET_CALL_DURATION":
      return { ...state, callDuration: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_ONLINE_USERS":
      return { ...state, onlineUsers: action.payload };
    case "RESET_CALL_STATE":
      console.log("Resetting call state, user role:", state.user?.role);
      const newScreen =
        state.user?.role === "normal" ? "main" : "main-provider";
      return {
        ...state,
        screen: newScreen,
        targetUserId: null,
        callerId: null,
        conversationId: null,
        isAudioMuted: false,
        callDuration: 0,
      };
    case "TOGGLE_TTS":
      return { ...state, ttsEnabled: !state.ttsEnabled };
    case "UPDATE_PROFILE_PHOTO":
      return {
        ...state,
        user: state.user ? { ...state.user, profile_photo_url: action.payload } : null,
      };
    default:
      return state;
  }
}

// Context interface
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  wsService: WebSocketService;
  webrtcService: WebRTCService;
  initializeSession: (token: string, connectWebSocket?: boolean) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setConversationId: (conversationId: string) => void;
  initiateCall: (
    targetUserId: string,
    conversationId?: string
  ) => Promise<void>;
  acceptCall: (conversationId?: string) => Promise<void>;
  rejectCall: (conversationId?: string) => void;
  cancelCall: (conversationId?: string) => void;
  hangUp: (conversationId?: string) => void;
  toggleMute: () => void;
  toggleTTS: () => void;
}

// Create Context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const stateRef = useRef(state);
  const wsService = useRef(new WebSocketService()).current;
  const webrtcService = useRef(new WebRTCService()).current;

  // Update stateRef
  useEffect(() => {
    stateRef.current = state;
    
    // Sync TTS enabled state
    const ttsService = TTSService.getInstance();
    ttsService.setEnabled(state.ttsEnabled);
  }, [state.ttsEnabled]);

  // Set WebSocket connection established callback
  useEffect(() => {
    wsService.onConnectionEstablished(() => {
      // When WebSocket connection is established, automatically send user-login event if user is logged in
      const currentUserId = stateRef.current.currentUserId;
      if (currentUserId) {
        console.log(
          "WebSocket reconnected, sending user-login for:",
          currentUserId
        );
        wsService.sendMessage("user-login", { userId: currentUserId });
      }
    });
  }, [wsService]);

  // Check and restore session
  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        dispatch({ type: "SET_SCREEN", payload: "login" });
        return;
      }

      try {
        // Verify if token is expired
        const decoded: any = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        if (decoded.exp && decoded.exp < currentTime) {
          // Token has expired, remove it
          localStorage.removeItem("token");
          dispatch({ type: "SET_SCREEN", payload: "login" });
          return;
        }

        const user = decoded.id || decoded.userId || decoded.sub;
        if (!user) {
          localStorage.removeItem("token");
          dispatch({ type: "SET_SCREEN", payload: "login" });
          return;
        }

        // Get user detailed information
        const userInfoRes = await fetch(API_ENDPOINTS.USERS_INFO, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const userInfoData = await userInfoRes.json();
        if (!userInfoRes.ok) {
          throw new Error(
            userInfoData.error || "Failed to get user information"
          );
        }

        // Update user information
        const userPayload = {
          id: user,
          name: userInfoData.name || "user",
          email: userInfoData.email,
          role:
            (userInfoData.type as "therapist" | "normal") ||
            "normal",
          has_paid: userInfoData.has_paid || false,
          profile_data: userInfoData.profile_data, // Include therapist profile data
          profile_photo_url: userInfoData.profile_photo_url, // Include profile photo URL
        };
        dispatch({
          type: "SET_USER",
          payload: userPayload,
        });
        dispatch({ type: "SET_CURRENT_USER", payload: user });

        // Set initial screen based on user role
        const initialScreen =
          userInfoData.type === "normal" ? "main" : "main-provider";
        dispatch({ type: "SET_SCREEN", payload: initialScreen });

        // Connect WebSocket (connection established callback will automatically send user-login)
        await wsService.connect();
      } catch (error) {
        console.error("Failed to restore session:", error);
        localStorage.removeItem("token");
        dispatch({ type: "SET_SCREEN", payload: "login" });
      }
    };

    restoreSession();
  }, [wsService]);

  // Handle call accepted
  const handleCallAccepted = useCallback(async () => {
    try {
      await webrtcService.initializePeerConnection();
      const offer = await webrtcService.createOffer();
      wsService.sendMessage("offer", {
        targetUserId: stateRef.current.targetUserId,
        offer,
      });
    } catch (error) {
      console.error("Failed to create offer:", error);
      dispatch({
        type: "SET_ERROR",
        payload: "Failed to establish call connection",
      });
    }
  }, [webrtcService, wsService]);

  // Handle call ended
  const handleCallEnd = useCallback(() => {
    webrtcService.endCall();
    dispatch({ type: "RESET_CALL_STATE" });
  }, [webrtcService]);

  // Call duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (state.screen === "in-call") {
      interval = setInterval(() => {
        dispatch({
          type: "SET_CALL_DURATION",
          payload: state.callDuration + 1,
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [state.screen, state.callDuration]);

  // WebSocket and WebRTC event handling - setup only once
  useEffect(() => {
    // Login response
    wsService.onMessage("login-success", (data: LoginData) => {
      dispatch({ type: "SET_CURRENT_USER", payload: data.userId });
      dispatch({ type: "SET_ERROR", payload: null });
    });

    wsService.onMessage("login-error", (data: ErrorData) => {
      dispatch({ type: "SET_ERROR", payload: data.message });
    });

    // Call events
    wsService.onMessage("incoming-call", (data: CallData) => {
      if (data.callerId) {
        dispatch({ type: "SET_CALLER", payload: data.callerId });
        if (data.conversationId) {
          dispatch({
            type: "SET_CONVERSATION_ID",
            payload: data.conversationId,
          });
        }
        dispatch({ type: "SET_SCREEN", payload: "incoming" });
      }
    });

    wsService.onMessage("call-accepted", (data: CallData) => {
      if (data.targetUserId) {
        dispatch({ type: "SET_TARGET_USER", payload: data.targetUserId });
        if (data.conversationId) {
          dispatch({
            type: "SET_CONVERSATION_ID",
            payload: data.conversationId,
          });
        }
        handleCallAccepted();
      }
    });

    wsService.onMessage("call-rejected", (data: CallData) => {
      dispatch({ type: "RESET_CALL_STATE" });
    });

    wsService.onMessage("call-cancelled", (data: CallData) => {
      dispatch({ type: "RESET_CALL_STATE" });
    });

    wsService.onMessage("call-started", (data: CallData) => {
      if (data.callerId) {
        dispatch({ type: "SET_TARGET_USER", payload: data.callerId });
        if (data.conversationId) {
          dispatch({
            type: "SET_CONVERSATION_ID",
            payload: data.conversationId,
          });
        }
        dispatch({ type: "SET_SCREEN", payload: "in-call" });
      }
    });

    wsService.onMessage("call-ended", (data: CallData) => {
      handleCallEnd();
    });

    wsService.onMessage("call-error", (data: ErrorData) => {
      dispatch({ type: "RESET_CALL_STATE" });
    });

    // WebRTC signaling
    wsService.onMessage("offer", async (data: WebRTCSignalData) => {
      if (data.offer) {
        try {
          await webrtcService.initializePeerConnection();
          const answer = await webrtcService.createAnswer(data.offer);
          wsService.sendMessage("answer", {
            targetUserId: data.fromUserId,
            answer,
          });
        } catch (error) {
          console.error("Failed to handle offer:", error);
        }
      }
    });

    wsService.onMessage("answer", async (data: WebRTCSignalData) => {
      if (data.answer) {
        try {
          await webrtcService.handleAnswer(data.answer);
          dispatch({ type: "SET_SCREEN", payload: "in-call" });
        } catch (error) {
          console.error("Failed to handle answer:", error);
        }
      }
    });

    wsService.onMessage("ice-candidate", async (data: WebRTCSignalData) => {
      if (data.candidate) {
        try {
          await webrtcService.addIceCandidate(data.candidate);
        } catch (error) {
          console.error("Failed to handle ICE candidate:", error);
        }
      }
    });

    wsService.onMessage("online-users", (data: OnlineUsersData) => {
      dispatch({ type: "SET_ONLINE_USERS", payload: data.users });
    });

    // WebRTC event handling
    webrtcService.onIceCandidate((candidate) => {
      wsService.sendMessage("ice-candidate", {
        targetUserId:
          stateRef.current.targetUserId || stateRef.current.callerId,
        candidate,
      });
    });

    webrtcService.onRemoteStream((stream) => {
      // Play remote audio stream
      const remoteAudio = document.getElementById(
        "remote-audio"
      ) as HTMLAudioElement;
      if (remoteAudio) {
        remoteAudio.srcObject = stream;
      }
    });

    webrtcService.onConnectionStateChange((connectionState) => {
      console.log("WebRTC connection state change:", connectionState);
      dispatch({
        type: "SET_CONNECTED",
        payload: connectionState === "connected",
      });
    });

    // Only disconnect on component unmount
    return () => {
      wsService.disconnect();
      webrtcService.endCall();
    };
  }, [wsService, webrtcService, handleCallAccepted, handleCallEnd]);

  // API methods
  // Shared session initialization logic - used by both login and registration
  const initializeSession = async (token: string, connectWebSocket: boolean = true): Promise<void> => {
    // 1. Parse JWT to get userId
    const decoded: any = jwtDecode(token);
    const userId = decoded.id || decoded.userId || decoded.sub;
    if (!userId) {
      dispatch({ type: "SET_ERROR", payload: "Invalid token" });
      throw new Error("Invalid token");
    }

    // 2. Get user detailed information
    const userInfoRes = await fetch(API_ENDPOINTS.USERS_INFO, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    const userInfoData = await userInfoRes.json();
    if (!userInfoRes.ok) {
      dispatch({
        type: "SET_ERROR",
        payload: userInfoData.error || "Failed to get user information",
      });
      throw new Error(userInfoData.error || "Failed to get user information");
    }

    // 3. Set user information and ID
    dispatch({
      type: "SET_USER",
      payload: {
        id: userId,
        name: userInfoData.name || "user",
        email: userInfoData.email,
        role:
          (userInfoData.type as "therapist" | "normal") ||
          "normal",
        has_paid: userInfoData.has_paid || false,
        profile_data: userInfoData.profile_data, // Include therapist profile data
        profile_photo_url: userInfoData.profile_photo_url, // Include profile photo URL
      },
    });
    dispatch({ type: "SET_CURRENT_USER", payload: userId });

    // 4. Set initial screen based on user role
    const initialScreen =
      userInfoData.type === "normal" ? "main" : "main-provider";
    dispatch({ type: "SET_SCREEN", payload: initialScreen });

    // 5. WebSocket login (optional - registration flow may skip this)
    if (connectWebSocket) {
      await wsService.connect();
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    let errorAlreadySet = false;
    try {
      // 1. RESTful login
      const res = await fetch(API_ENDPOINTS.LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        errorAlreadySet = true;
        dispatch({ type: "SET_ERROR", payload: data.error || "Login failed" });
        throw new Error(data.error || "Login failed");
      }
      // 2. Store token
      localStorage.setItem("token", data.token);

      // 3. Initialize session (fetch user info, set state, connect WebSocket)
      await initializeSession(data.token, true);
    } catch (error) {
      // Don't override the specific error message that was already set
      // Only set a generic error if no specific error was set
      if (!errorAlreadySet) {
        dispatch({ type: "SET_ERROR", payload: "Failed to connect to server" });
      }
      throw error;
    }
  };

  const logout = () => {
    wsService.disconnect();
    webrtcService.endCall();
    localStorage.removeItem("token");
    sessionStorage.removeItem("conversationId");
    dispatch({ type: "SET_CURRENT_USER", payload: null });
    dispatch({ type: "SET_SCREEN", payload: "login" });
  };

  const initiateCall = async (
    targetUserId: string,
    conversationId?: string
  ): Promise<void> => {
    try {
      // Get microphone permission
      await webrtcService.getLocalAudioStream();

      const callConversationId = conversationId || state.conversationId || "";
      dispatch({ type: "SET_TARGET_USER", payload: targetUserId });
      dispatch({ type: "SET_SCREEN", payload: "calling" });
      wsService.sendMessage("initiate-call", {
        targetUserId,
        conversationId: callConversationId,
      });
    } catch (error) {
      console.error("Failed to initiate call:", error);
      dispatch({
        type: "SET_ERROR",
        payload: "Cannot access microphone, please check permissions",
      });
      throw error;
    }
  };

  const acceptCall = async (conversationId?: string): Promise<void> => {
    try {
      if (!state.callerId) return;

      // Get microphone permission
      await webrtcService.getLocalAudioStream();

      const callConversationId = conversationId || state.conversationId || "";
      wsService.sendMessage("accept-call", {
        callerId: state.callerId,
        conversationId: callConversationId,
      });
      dispatch({ type: "SET_SCREEN", payload: "in-call" });
    } catch (error) {
      console.error("Failed to accept call:", error);
      dispatch({
        type: "SET_ERROR",
        payload: "Cannot access microphone, please check permissions",
      });
      rejectCall();
      throw error;
    }
  };

  const rejectCall = (conversationId?: string) => {
    if (!state.callerId) return;
    const callConversationId = conversationId || state.conversationId || "";
    wsService.sendMessage("reject-call", {
      callerId: state.callerId,
      conversationId: callConversationId,
    });
    dispatch({ type: "RESET_CALL_STATE" });
  };

  const cancelCall = (conversationId?: string) => {
    if (!state.targetUserId) return;
    const callConversationId = conversationId || state.conversationId || "";
    wsService.sendMessage("cancel-call", {
      targetUserId: state.targetUserId,
      conversationId: callConversationId,
    });
    dispatch({ type: "RESET_CALL_STATE" });
  };

  const hangUp = (conversationId?: string) => {
    const userId = state.targetUserId || state.callerId;
    if (!userId) return;
    const callConversationId = conversationId || state.conversationId || "";
    wsService.sendMessage("hang-up", {
      targetUserId: userId,
      conversationId: callConversationId,
    });
    dispatch({ type: "RESET_CALL_STATE" });
  };

  const toggleMute = () => {
    const isMuted = webrtcService.toggleMute();
    dispatch({ type: "SET_AUDIO_MUTED", payload: isMuted });
  };

  const toggleTTS = () => {
    dispatch({ type: "TOGGLE_TTS" });
  };

  const setConversationId = (conversationId: string) => {
    dispatch({ type: "SET_CONVERSATION_ID", payload: conversationId });
  };

  const contextValue: AppContextType = {
    state,
    dispatch,
    wsService,
    webrtcService,
    initializeSession,
    login,
    logout,
    setConversationId,
    initiateCall,
    acceptCall,
    rejectCall,
    cancelCall,
    hangUp,
    toggleMute,
    toggleTTS,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// Custom Hook
export const useApp = (): AppContextType => {
  // console.log(AppContext);
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
